import { useUnit } from 'effector-react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { $activeInstruments } from '../../entities/instrument';
import { openPositionFx } from '../../entities/position';
import type { Schemas } from '../../shared/api/client';
import { cn } from '../../shared/lib/cn';
import {
  formatMoney,
  formatNumber,
  formatRatio,
  formatShares,
  placesOf,
} from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { shortcutOf } from '../../shared/lib/shortcuts';
import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  Field,
  Input,
  Select,
  Shortcut,
  Spinner,
  type Column,
} from '../../shared/ui';
import {
  $advanced,
  $analogs,
  $draft,
  $errors,
  $open,
  $preview,
  $submitAttempted,
  advancedToggled,
  analogsFx,
  dialogClosed,
  fieldChanged,
  previewFx,
  submitted,
  type DraftField,
} from './model';

export function OpenPositionDialog() {
  const { t } = useTranslation();
  const [open, close, submit, pending] = useUnit([
    $open,
    dialogClosed,
    submitted,
    openPositionFx.pending,
  ]);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      {open && (
        <DialogContent
          wide
          title={t('openPosition.title')}
          description={t('openPosition.description')}
          closeLabel={t('common.close')}
          onSubmit={submit}
          footer={
            <>
              <Button onClick={close}>{t('common.cancel')}</Button>
              <Button type="submit" variant="primary" disabled={pending}>
                {t('openPosition.submit')}
                <Shortcut keys={shortcutOf('submit')} className="ml-1 opacity-80" />
              </Button>
            </>
          }
        >
          <DraftFields />
          <LiveHint />
        </DialogContent>
      )}
    </Dialog>
  );
}

function DraftFields() {
  const { t } = useTranslation();
  const [draft, errors, attempted, advanced, toggle, change, instruments] = useUnit([
    $draft,
    $errors,
    $submitAttempted,
    $advanced,
    advancedToggled,
    fieldChanged,
    $activeInstruments,
  ]);
  const required = (field: DraftField): string | undefined =>
    errors[field] ?? (attempted && draft[field].trim() === '' ? t('form.required') : undefined);

  const numberField = (field: DraftField, label: string, unit: string, hint?: string) => (
    <Field label={label} error={required(field)} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <Input
          id={id}
          inputMode="decimal"
          autoComplete="off"
          numeric
          unit={unit}
          value={draft[field]}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onChange={(event) => {
            change({ field, value: event.target.value });
          }}
        />
      )}
    </Field>
  );

  return (
    <div className="space-y-2">
      <Field label={t('openPosition.instrument')} error={required('instrumentId')}>
        {({ id, describedBy, invalid }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            placeholder={t('openPosition.pickInstrument')}
            value={draft.instrumentId || undefined}
            options={instruments.map((i) => ({
              value: String(i.id),
              text: `${i.display_symbol} ${i.name}`,
              label: (
                <span className="flex min-w-0 items-center gap-2">
                  <span className="font-medium">{i.display_symbol}</span>
                  <span className="truncate text-text-3">{i.name}</span>
                </span>
              ),
            }))}
            onValueChange={(value) => {
              change({ field: 'instrumentId', value });
            }}
          />
        )}
      </Field>
      {numberField('price', t('openPosition.price'), '€', t('openPosition.priceHint'))}
      {numberField('initialSum', t('openPosition.initialSum'), '€')}
      {numberField('target', t('openPosition.target'), '%')}

      <div className="pt-1">
        <button
          type="button"
          aria-expanded={advanced}
          onClick={toggle}
          className="flex h-6 items-center gap-1 rounded-md px-1 text-xs font-medium text-text-2 outline-none hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus"
        >
          <ChevronRight
            aria-hidden
            className={cn('size-3.5 transition-transform duration-150', advanced && 'rotate-90')}
          />
          {t('openPosition.ladderSettings')}
          {!advanced && (
            <span className="font-normal text-text-3">
              {t('openPosition.ladderSummary', {
                levels: draft.levels,
                step: draft.step,
                multiplier: draft.multiplier,
              })}
            </span>
          )}
        </button>
      </div>
      {advanced && (
        <div className="space-y-2">
          <Field label={t('openPosition.levels')} error={errors.levels}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={draft.levels}
                options={['1', '2', '3', '4', '5', '6'].map((n) => ({ value: n, label: n }))}
                onValueChange={(value) => {
                  change({ field: 'levels', value });
                }}
              />
            )}
          </Field>
          {numberField('step', t('openPosition.step'), '%', t('openPosition.stepHint'))}
          {numberField(
            'multiplier',
            t('openPosition.multiplier'),
            '×',
            t('openPosition.multiplierHint'),
          )}
        </div>
      )}
    </div>
  );
}

function LiveHint() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [preview, loading, instruments, draft] = useUnit([
    $preview,
    previewFx.pending,
    $activeInstruments,
    $draft,
  ]);
  const instrument = instruments.find((i) => String(i.id) === draft.instrumentId);
  const places = instrument ? placesOf(instrument.tick_size) : 2;

  const columns: Column<Schemas['LevelPlanOut']>[] = [
    {
      id: 'n',
      header: t('ladder.level'),
      cell: (row) => (row.n === 0 ? t('ladder.entry') : t('ladder.levelN', { n: row.n })),
    },
    {
      id: 'price',
      header: t('ladder.price'),
      numeric: true,
      cell: (row) => formatMoney(row.price, locale, { places }),
    },
    {
      id: 'shares',
      header: t('ladder.shares'),
      numeric: true,
      cell: (row) => formatShares(row.shares, locale),
    },
    {
      id: 'cost',
      header: t('ladder.cost'),
      numeric: true,
      cell: (row) => formatMoney(row.cost, locale),
    },
    {
      id: 'cumulative',
      header: t('ladder.cumulative'),
      numeric: true,
      cell: (row) => formatMoney(row.cumulative_cost, locale),
    },
    {
      id: 'target',
      header: t('ladder.targetIfFilled'),
      numeric: true,
      cell: (row) => formatMoney(row.target_price, locale, { places }),
    },
  ];

  return (
    <div className="mt-4 rounded-lg border border-border-subtle bg-bg-1">
      <div className="flex h-9 items-center justify-between px-3">
        <h3 className="text-xs font-medium text-text-2">{t('openPosition.plan')}</h3>
        {loading && <Spinner label={t('common.loading')} />}
      </div>
      {preview ? (
        <>
          <div className="overflow-x-auto px-1">
            <DataTable
              dense
              caption={t('openPosition.plan')}
              columns={columns}
              rows={preview.levels}
              rowKey={(row) => row.n}
            />
          </div>
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-t border-border-subtle px-3 py-2.5">
            <span className="text-xs text-text-3">{t('openPosition.capitalNeeded')}</span>
            <span className="tabular text-base font-semibold">
              {formatMoney(preview.total_needed, locale)}
            </span>
          </div>
          {Number(preview.leftover) > 0 && (
            <p className="px-3 pb-2 text-xs text-text-3">
              {t('openPosition.leftover', {
                shares: formatShares(preview.initial_shares, locale),
                leftover: formatMoney(preview.leftover, locale),
              })}
            </p>
          )}
          <AnalogSentence />
        </>
      ) : (
        <p className="px-3 pb-3 text-sm text-text-3">{t('openPosition.planEmpty')}</p>
      )}
    </div>
  );
}

function AnalogSentence() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [analogs, loading] = useUnit([$analogs, analogsFx.pending]);
  if (loading && !analogs) {
    return (
      <p className="border-t border-border-subtle px-3 py-2.5 text-xs text-text-3">
        {t('analogs.loading')}
      </p>
    );
  }
  const stats = analogs?.analog;
  if (!stats || stats.entries === 0) return null;
  const regime = analogs.regime;
  const conditions = [
    regime?.trend
      ? t(regime.trend === 'up' ? 'regime.uptrendLower' : 'regime.downtrendLower')
      : null,
    regime?.vol_band ? t(`regime.volLower.${regime.vol_band}`) : null,
  ]
    .filter(Boolean)
    .join(', ');
  const level2 = stats.reached[1];
  const all = stats.reached[stats.reached.length - 1];
  return (
    <div className="space-y-1 border-t border-border-subtle px-3 py-2.5 text-xs">
      <p className="text-text-2">
        {t('analogs.sentence', {
          conditions: conditions || t('analogs.anyConditions'),
          level2: level2 === undefined ? '—' : formatRatio(level2, locale),
          all: all === undefined ? '—' : formatRatio(all, locale),
          median: stats.median_days === null ? '—' : formatNumber(stats.median_days, locale),
          worst: stats.max_days === null ? '—' : formatNumber(stats.max_days, locale),
        })}
      </p>
      <p className="text-text-3">
        {t('analogs.caveat', { entries: formatNumber(stats.entries, locale), years: stats.years })}
      </p>
    </div>
  );
}
